/**
 * Chart visualization types for SQL Studio.
 * Used by both Query Editor and Table Explorer.
 */

// ============================================================================
// Chart Type
// ============================================================================

/** Supported chart types */
export type ChartType = 'bar' | 'line' | 'pie' | 'area' | 'scatter';

export const CHART_TYPES: ChartType[] = ['bar', 'line', 'pie', 'area', 'scatter'];

export const CHART_TYPE_LABELS: Record<ChartType, string> = {
  bar: 'Bar Chart',
  line: 'Line Chart',
  pie: 'Pie Chart',
  area: 'Area Chart',
  scatter: 'Scatter Plot',
};

// ============================================================================
// Chart Variants (Subtypes)
// ============================================================================

/** Bar chart display variants */
export type BarChartVariant = 'grouped' | 'stacked';

/**
 * Horizontal target/goal line drawn on cartesian charts. Used by dashboard
 * chart tiles (S3); severity picks the status color, never the palette.
 */
export type ChartReferenceLine = {
  value: number;
  label?: string;
  severity?: 'warning' | 'critical';
};

export const BAR_CHART_VARIANTS: BarChartVariant[] = ['grouped', 'stacked'];

export const BAR_CHART_VARIANT_LABELS: Record<BarChartVariant, string> = {
  grouped: 'Side by Side',
  stacked: 'Stacked',
};

export const BAR_CHART_VARIANT_DESCRIPTIONS: Record<BarChartVariant, string> = {
  grouped: 'Each series shown as separate bars next to each other',
  stacked: 'Series stacked on top of each other to show total',
};

/** Area chart display variants */
export type AreaChartVariant = 'overlapping' | 'stacked';

export const AREA_CHART_VARIANTS: AreaChartVariant[] = ['overlapping', 'stacked'];

export const AREA_CHART_VARIANT_LABELS: Record<AreaChartVariant, string> = {
  overlapping: 'Overlapping',
  stacked: 'Stacked',
};

export const AREA_CHART_VARIANT_DESCRIPTIONS: Record<AreaChartVariant, string> = {
  overlapping: 'Areas overlap, each showing absolute values',
  stacked: 'Areas stacked to show cumulative totals',
};

// ============================================================================
// Aggregation
// ============================================================================

/** Aggregation functions for numeric columns */
export type AggregationFunction = 'sum' | 'avg' | 'min' | 'max' | 'count' | 'none';

export const AGGREGATION_FUNCTIONS: AggregationFunction[] = [
  'none',
  'sum',
  'avg',
  'min',
  'max',
  'count',
];

export const AGGREGATION_LABELS: Record<AggregationFunction, string> = {
  none: 'None',
  sum: 'Sum',
  avg: 'Average',
  min: 'Minimum',
  max: 'Maximum',
  count: 'Count',
};

// ============================================================================
// Column Mapping
// ============================================================================

/** Column mapping for chart axes */
export type ColumnMapping = {
  /** Column name from result set */
  column: string;
  /** Optional label override for display */
  label?: string;
};

/** Column mapping with aggregation (for Y axis / values) */
export type AggregatedColumnMapping = ColumnMapping & {
  /** Aggregation function to apply */
  aggregation: AggregationFunction;
};

/** Series definition for multi-series charts */
export type SeriesMapping = {
  /** Column used for series grouping */
  column: string;
  /** Optional color map for specific series values */
  colors?: Record<string, string>;
};

// ============================================================================
// Chart Configuration
// ============================================================================

/** Complete chart configuration - persisted per result set */
export type ChartConfig = {
  /** Chart type */
  type: ChartType;
  /** X-axis column mapping (category/time axis) */
  xAxis: ColumnMapping;
  /** Y-axis column mappings (one or more value columns) */
  yAxis: AggregatedColumnMapping[];
  /** Optional series column for grouping */
  series?: SeriesMapping;
  /** Chart title */
  title?: string;
  /** Show legend */
  showLegend: boolean;
  /** Show grid lines */
  showGrid: boolean;
  /** Custom color palette */
  colors?: string[];
  /** Bar chart variant (grouped or stacked) - only used when type='bar' */
  barVariant?: BarChartVariant;
  /** Area chart variant (overlapping or stacked) - only used when type='area' */
  areaVariant?: AreaChartVariant;
};

/** Default chart colors (Tailwind-inspired palette) */
export const DEFAULT_CHART_COLORS = [
  '#3b82f6', // blue-500
  '#22c55e', // green-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
  '#ec4899', // pink-500
  '#14b8a6', // teal-500
  '#6366f1', // indigo-500
];

// ============================================================================
// Transformed Data Types
// ============================================================================

/** Transformed data point for charts */
export type ChartDataPoint = Record<string, string | number | null>;

/**
 * Reserved datum key carrying the RAW x value of each point's group (S11).
 * X labels are locale-formatted for display — a filter built from them
 * breaks on non-English locales and timezones, so mark events resolve the
 * value from this key when present. Date axes encode granularity here:
 * bare `YYYY-MM-DD` for day-grouped, full ISO instant for time-grained.
 * Views never enumerate datum keys (they iterate `seriesKeys`), so the
 * extra key is render-inert.
 */
export const CHART_RAW_X_KEY = '__rawX';

/** Transformed chart data ready for Recharts */
export type TransformedChartData = {
  /** Data points */
  data: ChartDataPoint[];
  /** All series keys present in data (Y columns or series values) */
  seriesKeys: string[];
  /** X-axis data key name */
  xDataKey: string;
  /** Whether X-axis is numeric (for scatter) */
  xIsNumeric: boolean;
};

/**
 * A resolved mark interaction (S11) — the stable contract dashboards consume
 * for cross-filter/drill. `field` is always the RAW ChartConfig column (never
 * the display label); `value` is the datum's (display-formatted) axis value.
 */
export type ChartMarkEvent = {
  field: string;
  value: string | number | null;
  seriesField?: string;
  seriesValue?: string;
};

/** What a chart view can normalize out of a Recharts click, pre-resolution. */
export type RawMarkClick = {
  datum: Record<string, unknown> | null;
  /** dataKey/name of the clicked Bar/Line/Area series (pie has none). */
  seriesKey?: string;
};

// ============================================================================
// Export Types
// ============================================================================

/** Chart export format */
export type ChartExportFormat = 'png' | 'svg';

/** Chart export request */
export type ChartExportRequest = {
  format: ChartExportFormat;
  /** SVG content to export */
  svgContent: string;
  /** Suggested file name */
  suggestedFileName?: string;
  /** Optional dialog title */
  title?: string;
  /** Direct file path (bypasses save dialog) */
  filePath?: string;
};

/** Chart export result */
export type ChartExportResult =
  | { success: true; filePath: string }
  | { success: false; error: string; cancelled?: boolean };

// ============================================================================
// Column Type Info
// ============================================================================

/** Detected column data type category. `unsupported` covers spatial/geometry
 *  columns — they are neither categorical nor numeric and are excluded from
 *  every chart axis/series candidate list (spatial support Task 27). */
export type ColumnDataCategory = 'numeric' | 'date' | 'string' | 'boolean' | 'unknown' | 'unsupported';

/**
 * Column with detected type info. `key` is the ROW key (`QueryColumn.id ?? name`
 * — `col_<i>` for a duplicate or blank name) and is what every `ChartConfig`
 * column reference holds; `name` is the display label (`Column N` for a blank
 * name) and is never used to read a row.
 */
export type ColumnTypeInfo = {
  key: string;
  name: string;
  dataType?: string;
  category: ColumnDataCategory;
  /** Percentage of non-null values that match the category */
  confidence: number;
};
